// Condition and out of service on the asset page (CR_20260921_Asset_Lifecycle T2, §7.1). The card shows the latest
// word, who gave it and where ("Fair · 18 Sep by Kumar (stock count)"), and the last few as a trend; the full history is
// the Lifecycle tab — never a second history view here. It appears once there is a report; before that, "Report
// condition" is in the page's ⋮ menu. Out of service is a banner above the tabs and a menu action (§4.5).
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  type AssetCondition,
  CONDITIONS,
  conditionTrend,
  DOC_TYPES,
  docProblem,
} from '@fleetspoke/core/lifecycle';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConditionReports, useReportCondition, useSetServiceState } from '../../data/condition';
import type { AssetOverview } from '../../data/queries';
import { calendarDate } from '../../lib/time';
import { Card, CardHeader } from './Card';

const TONE: Record<AssetCondition, 'success' | 'default' | 'warning' | 'error'> = {
  good: 'success',
  fair: 'default',
  poor: 'warning',
  unserviceable: 'error',
};

export function ConditionChip({ condition }: { condition: AssetCondition }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={TONE[condition]}
      variant={condition === 'good' || condition === 'fair' ? 'outlined' : 'filled'}
      label={t(`condition.${condition}`)}
      data-testid={`condition-${condition}`}
    />
  );
}

export function ConditionCard({
  asset,
  onReport,
  onHistory,
}: {
  asset: AssetOverview;
  onReport?: () => void;
  onHistory: () => void;
}) {
  const { t, i18n } = useTranslation();
  const reports = useConditionReports(asset.asset_id);
  const latest = reports.data?.[0];
  if (!asset.condition || !latest) return null;
  const trend = conditionTrend(reports.data ?? []);
  return (
    <Card data-testid="asset-condition">
      <CardHeader
        title={t('condition.title')}
        aside={
          <Stack direction="row" spacing={1}>
            {onReport ? (
              <Button size="small" onClick={onReport}>
                {t('condition.report')}
              </Button>
            ) : null}
            <Button size="small" onClick={onHistory}>
              {t('condition.history')}
            </Button>
          </Stack>
        }
      />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <ConditionChip condition={asset.condition} />
        <Typography variant="body2" color="text.secondary">
          {t('condition.latest', {
            condition: t(`condition.${latest.condition}`),
            date: calendarDate(latest.at.slice(0, 10), i18n.language) ?? '',
            by: latest.by_name ?? '—',
            source: t(`condition.src_${latest.source}`),
          })}
        </Typography>
      </Stack>
      {latest.note ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          “{latest.note}”
        </Typography>
      ) : null}
      {trend.length > 1 ? (
        <Typography variant="caption" color="text.secondary" data-testid="condition-trend">
          {trend.map((c) => t(`condition.${c}`)).join(' ▸ ')}
        </Typography>
      ) : null}
    </Card>
  );
}

export function ConditionDialog({ asset, onClose }: { asset: AssetOverview; onClose(): void }) {
  const { t } = useTranslation();
  const report = useReportCondition(asset.company_id, asset.asset_id);
  const [condition, setCondition] = useState<AssetCondition | null>(null);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const problem = photo ? docProblem(photo) : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (condition) report.mutate({ condition, note, photo }, { onSuccess: onClose });
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{`${t('condition.report')} · ${asset.asset_code}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">{t('condition.pick')}</Typography>
            <ToggleButtonGroup
              exclusive
              value={condition}
              onChange={(_, v: AssetCondition | null) => setCondition(v)}
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              {CONDITIONS.map((c) => (
                <ToggleButton
                  key={c}
                  value={c}
                  data-testid={`pick-${c}`}
                  sx={{ textTransform: 'none' }}
                >
                  {t(`condition.${c}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              label={t('condition.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <Box>
              <Button component="label" variant="outlined" size="small">
                {t('condition.photo')}
                <input
                  hidden
                  type="file"
                  accept={DOC_TYPES.filter((x) => x.startsWith('image/')).join(',')}
                  onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {photo?.name ?? ''}
              </Typography>
              {problem ? (
                <Typography variant="caption" color="error" component="div">
                  {t(problem === 'too_big' ? 'certs.file_too_big' : 'certs.file_type')}
                </Typography>
              ) : null}
            </Box>
            {report.error ? (
              <Alert severity="error">
                {t('common.error_generic')} {(report.error as Error).message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!condition || !!problem || report.isPending}
            data-testid="condition-save"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Mark out of service (a reason is required) or back in service — it reads differently from Remove from service. */
export function ServiceStateDialog({ asset, onClose }: { asset: AssetOverview; onClose(): void }) {
  const { t } = useTranslation();
  const set = useSetServiceState(asset.asset_id);
  const out = asset.service_state !== 'out_of_service';
  const [reason, setReason] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`${t(out ? 'service.mark_out' : 'service.mark_in')} · ${asset.asset_code}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {out ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('service.mark_out_help')}
              </Typography>
              <TextField
                label={t('service.reason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                autoFocus
                slotProps={{ htmlInput: { maxLength: 300, 'data-testid': 'service-reason' } }}
              />
            </>
          ) : (
            <Typography variant="body2">
              {t('service.banner_short', { reason: asset.service_reason ?? '—' })}
            </Typography>
          )}
          {set.error ? (
            <Alert severity="error">
              {t('common.error_generic')} {(set.error as Error).message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color={out ? 'warning' : 'primary'}
          disabled={(out && !reason.trim()) || set.isPending}
          onClick={() => set.mutate({ out, reason }, { onSuccess: onClose })}
          data-testid="service-save"
        >
          {t(out ? 'service.mark_out' : 'service.mark_in')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ServiceBanner({ asset }: { asset: AssetOverview }) {
  const { t } = useTranslation();
  if (asset.service_state !== 'out_of_service') return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid="service-banner">
      {t('service.banner_short', { reason: asset.service_reason ?? '—' })}
    </Alert>
  );
}
